import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useApiUrl } from './ApiContext';

function useRecaptchaV3(siteKey) {
  const [state, setState] = useState({ ready: false, error: null });

  useEffect(() => {
    if (!siteKey) {
      setState({ ready: false, error: 'Missing site key' });
      return;
    }

    if (window.grecaptcha?.execute) {
      window.grecaptcha.ready(() => setState({ ready: true, error: null }));
      return;
    }

    const id = 'grecaptcha-script';
    if (document.getElementById(id)) {
      const iv = setInterval(() => {
        if (window.grecaptcha?.execute) {
          clearInterval(iv);
          window.grecaptcha.ready(() => setState({ ready: true, error: null }));
        }
      }, 100);
      return () => clearInterval(iv);
    }

    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.defer = true;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => setState({ ready: true, error: null }));
      } else {
        setState({ ready: false, error: 'grecaptcha not available after load' });
      }
    };
    s.onerror = () => setState({ ready: false, error: 'Failed to load reCAPTCHA script' });
    document.body.appendChild(s);
  }, [siteKey]);

  return state;
}

async function getRecaptchaToken(siteKey, action, timeoutMs = 10000) {
  if (!window.grecaptcha?.execute) {
    throw new Error('grecaptcha.execute not available');
  }
  let timeout;
  try {
    const tokenPromise = window.grecaptcha.execute(siteKey, { action });
    const timeoutPromise = new Promise((_, rej) => {
      timeout = setTimeout(() => rej(new Error('reCAPTCHA timeout')), timeoutMs);
    });
    const token = await Promise.race([tokenPromise, timeoutPromise]);
    if (!token) throw new Error('Empty reCAPTCHA token');
    return token;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

const MAX = { name: 120, email: 254, phone: 32, message: 5000 };

const removeInvisible = (s) =>
  s.replace(/[\u202A-\u202E\u2066-\u2069\u200B-\u200D\u2060]/g, '');

const baseSanitize = (s, { allowNewlines = false, max = 8000 } = {}) => {
  if (typeof s !== 'string') return '';
  try { s = s.normalize('NFKC'); } catch {}
  s = s.replace(allowNewlines ? /[\x00-\x09\x0B\x0C\x0E-\x1F\x7F]/g : /[\x00-\x1F\x7F]/g, '');
  s = removeInvisible(s);
  s = s.replace(/[<>\\`$|{}]/g, '');
  if (allowNewlines) {
    s = s.replace(/\r/g, '');
    s = s.replace(/\n{3,}/g, '\n\n');
  }
  s = s.trim().replace(/[ \t]{2,}/g, ' ');
  s = s.replace(/[-=]{5,}/g, '—');
  return s.slice(0, max);
};

const sanitizeName = (s) => baseSanitize(s, { max: MAX.name });
const sanitizeEmail = (s) => baseSanitize(s, { max: MAX.email }).toLowerCase();
const sanitizePhone = (s) => baseSanitize(s, { max: MAX.phone }).replace(/[^\d+]/g, '');
const sanitizeMessage = (s) => baseSanitize(s, { allowNewlines: true, max: MAX.message });

const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const validateUKPhone = (p) => /^(\+44\d{9,10}|0\d{9,10})$/.test(p);
const SuccessClearMS = 3000;

function Contact() {
  const apiBase = useApiUrl();

  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const { ready: recaptchaReady, error: recaptchaError } = useRecaptchaV3(SITE_KEY);

  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', company: '' });
  const [agree, setAgree] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [submitting, setSubmitting] = useState(false);
  const mapRef = useRef(null);
  const mapElRef = useRef(null);

  useEffect(() => {
    if (status.type === 'success' && status.msg) {
      const t = setTimeout(() => setStatus({ type: '', msg: '' }), SuccessClearMS);
      return () => clearTimeout(t);
    }
  }, [status, setStatus]);

  useEffect(() => {
    if (mapRef.current) return;

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow,
    });

    const CENTER = L.latLng(52.336681, 1.133230);

    const BOUNDS = L.latLngBounds(
      [CENTER.lat - 0.01, CENTER.lng - 0.01],
      [CENTER.lat + 0.01, CENTER.lng + 0.01]
    );

    const map = L.map(mapElRef.current, {
      center: CENTER,
      zoom: 16,
      minZoom: 14,
      maxZoom: 18,
      maxBounds: BOUNDS,
      maxBoundsViscosity: 1.0,
      inertia: false,
      zoomControl: true,
      scrollWheelZoom: true,
      touchZoom: true,
      dragging: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    L.marker(CENTER)
      .addTo(map)
      .bindPopup(
        `<div style="text-align:center;">
           <strong>Horizon Seeds</strong><br/>
           Unit 3 Airfield Ind Park<br/>
           Langton Green Eye<br/>
           Suffolk<br/>
           IP23 7HN
         </div>`,
        { closeButton: false }
      )
      .openPopup();

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const m = mapRef.current;
        if (m && m._mapPane) {
          try {
            m.invalidateSize({ animate: false });
          } catch {}
        }
      });
    });
    if (mapElRef.current) ro.observe(mapElRef.current);

    return () => {
      ro.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const canSubmit = useMemo(() => {
    const n = sanitizeName(form.name);
    const p = sanitizePhone(form.phone);
    const em = sanitizeEmail(form.email);
    const msg = sanitizeMessage(form.message);

    const fieldsValid =
      n.length > 0 &&
      validateUKPhone(p) &&
      validateEmail(em) &&
      msg.length >= 10 &&
      !form.company &&
      agree;

    const recaptchaOk = !SITE_KEY || (recaptchaReady && !recaptchaError);

    return fieldsValid && recaptchaOk && !submitting;
  }, [form, agree, submitting, SITE_KEY, recaptchaReady, recaptchaError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', msg: '' });

    if (!agree) {
      setStatus({ type: 'error', msg: 'Please confirm you have read and understand the Privacy Policy.' });
      return;
    }

    if (form.company && form.company.trim() !== '') {
      setStatus({ type: 'success', msg: 'Thank you! Your message has been sent.' });
      setForm({ name: '', phone: '', email: '', message: '', company: '' });
      setAgree(false);
      return;
    }

    const payload = {
      name: sanitizeName(form.name),
      phone: sanitizePhone(form.phone),
      email: sanitizeEmail(form.email),
      message: sanitizeMessage(form.message),
      company: '',
      recaptchaToken: '',
      recaptchaAction: 'contact',
    };

    if (!payload.name) return setStatus({ type: 'error', msg: 'Please provide your name.' });
    if (!validateUKPhone(payload.phone)) return setStatus({ type: 'error', msg: 'Please enter a valid UK telephone number (start with +44 or 0).' });
    if (!validateEmail(payload.email)) return setStatus({ type: 'error', msg: 'Please enter a valid email address.' });
    if (!payload.message || payload.message.length < 10) return setStatus({ type: 'error', msg: 'Please include a short message (at least 10 characters).' });

    if (SITE_KEY) {
      if (recaptchaError) {
        setStatus({ type: 'error', msg: 'reCAPTCHA failed to load. Check network/ad blocker and try again.' });
        return;
      }
      if (!recaptchaReady) {
        setStatus({ type: 'error', msg: 'reCAPTCHA not ready yet. Please try again in a moment.' });
        return;
      }
      try {
        payload.recaptchaToken = await getRecaptchaToken(SITE_KEY, 'contact');
      } catch (err) {
        setStatus({ type: 'error', msg: err?.message || 'Unable to verify authenticity. Please refresh and try again.' });
        return;
      }
    }

    try {
      setSubmitting(true);
      const url = `${apiBase.replace(/\/$/, '')}/contact`;
      const res = await axios.post(url, payload, {
        withCredentials: true,
        timeout: 20000,
        headers: { 'Content-Type': 'application/json' },
        validateStatus: () => true,
      });

      if (res.status < 200 || res.status >= 300) {
        throw new Error(res.data?.message || `Request failed (${res.status})`);
      }

      setStatus({ type: 'success', msg: 'Thank you! Your message has been sent.' });
      setForm({ name: '', phone: '', email: '', message: '', company: '' });
      setAgree(false);
    } catch (err) {
      setStatus({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-container">
      <header className="contact-header">
        <h1>Contact Us</h1>
        <p className="contact-subtitle">
          We would love to hear from you. Call, email, or send us a message.
        </p>
      </header>

      <section className="contact-grid">
        <div className="panel map-panel">
          <div className="panel-header">
            <h2>Our Office</h2>
            <p className="muted">Unit 3 Airfield Ind Park, Langton Green Eye, Suffolk, IP23 7HN</p>
          </div>

          <div className="inline-contact">
            <div>
              <strong>Telephone:</strong>{' '}
              <a href="tel:+441379873377" aria-label="Call Horizon Seeds on 01379 873 377">
                01379 873 377
              </a>
            </div>
            <div>
              <strong>Email:</strong>{' '}
              <a href="mailto:info@horizonseeds.com">info@horizonseeds.com</a>
            </div>
          </div>

          <div ref={mapElRef} id="map" className="contact-map" />
        </div>

        <div className="panel form-panel">
          <h2>Send Us a Message</h2>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="row">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={form.name}
                onChange={onChange}
              />
            </div>

            <div className="row two-col">
              <div>
                <label htmlFor="phone">Telephone</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  required
                  value={form.phone}
                  onChange={onChange}
                />
              </div>
              <div>
                <label htmlFor="email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="row message-row">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows={7}
                required
                value={form.message}
                onChange={onChange}
              />
            </div>

            <div className="row">
              <label className="checkbox-label" htmlFor="privacyAgree" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  id="privacyAgree"
                  name="privacyAgree"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  required
                />
                <span>
                  I have read and understand the{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            <div
              style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
              aria-hidden="true"
            >
              <label htmlFor="company">Company</label>
              <input
                id="company"
                name="company"
                type="text"
                value={form.company}
                onChange={onChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {status.msg ? (
              <p
                className={`form-status ${status.type === 'error' ? 'error' : 'success'}`}
                aria-live="polite"
              >
                {status.msg}
              </p>
            ) : null}

            <button  className="send-message-button" type="submit" disabled={!canSubmit}>
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Contact;
